import { EuiLink } from "@inscopix/ideas-eui";
import assert from "assert";
import { NotificationsFeedBodyQuery } from "graphql/_Types";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { isNonNull } from "utils/isNonNull";
import { NotificationAlert } from "./NotificationAlert";
import { useCallback } from "react";
import { isUndefined } from "lodash";
import { RegionBadge } from "components/RegionBadge/RegionBadge";
import { useFlyoutContext } from "providers/FlyoutProvider/FlyoutProvider";
import { ExternalRegion } from "ideas.env";

interface NotificationAlertTenantUserMembershipProps {
  data: NonNullable<
    NotificationsFeedBodyQuery["notifications"]
  >["nodes"][number] & { region?: ExternalRegion };
  onNotificationRead: (
    notificationId: string,
    region?: ExternalRegion,
    refetchNotifications?: () => void,
  ) => void;
  refetchNotifications: () => void;
}

/** Component that renders a notification that a user has been invited to a tenant */
export const NotificationAlertTenantUserMembership = ({
  data,
  onNotificationRead,
  refetchNotifications,
}: NotificationAlertTenantUserMembershipProps) => {
  const routeMap = useRouteMapContext((s) => s.routeMap);
  const { closeFlyout } = useFlyoutContext();
  const { dateCreated, hasSeen, id: notificationId } = data;
  const attrs = data.tenantUserMembershipNotification;
  assert(isNonNull(attrs));
  const { inviter, tenant } = attrs;
  assert(isNonNull(inviter) && isNonNull(tenant));
  const inviterFullName = `${inviter.firstName} ${inviter.lastName}`.trim();

  const tenantPath = routeMap["ORGANIZATION"].dynamicPath({
    tenantKey: tenant.key,
  }).path;

  const onNotificationReadMemoized = useCallback(() => {
    if (!hasSeen) {
      onNotificationRead(notificationId, data.region, refetchNotifications);
    }
  }, [
    hasSeen,
    notificationId,
    onNotificationRead,
    refetchNotifications,
    data.region,
  ]);

  const handleClick = (close = true) => {
    close && closeFlyout();
    onNotificationReadMemoized();
  };

  const action = isUndefined(data.region) ? (
    <EuiLink href={tenantPath} onClick={() => handleClick()}>
      Go to organization
    </EuiLink>
  ) : (
    <EuiLink
      href={`${data.region.URL}${tenantPath}`}
      target="_blank"
      onClick={() => handleClick(false)}
    >
      Go to organization <RegionBadge regionShortName={data.region.shortName} />
    </EuiLink>
  );

  return (
    <NotificationAlert
      dateCreated={dateCreated}
      hasSeen={hasSeen}
      title="Invited to organization"
      onNotificationRead={onNotificationReadMemoized}
      text={
        <span>
          You have been invited to the <strong>{tenant.name}</strong>{" "}
          organization by <strong>{inviterFullName}</strong>.
        </span>
      }
      action={action}
    />
  );
};
